/**
 * 用户信息 / 文件地址解析
 */
import request from '../xajax'
import type { ApiResponse } from '../interfaces'
import { APP_BASE_FILE_URL } from '@/config/global'

export interface AiUserInfo {
  id?: string
  aid?: string
  avatar?: string
  nickName?: string
  mobile?: string
  email?: string
  inviteCode?: string
  points?: number
  status?: number
  gender?: string
  regTime?: string
  sysCreateTime?: string
  sysUpdateTime?: string
  [key: string]: any
}

const base = '/client/user'

/**
 * 接口以后端 UserApi（xunis-client-api）为准，勿自行造端点：
 *   GET  /client/user/info          当前登录用户信息
 *   POST /client/user/update        资料修改（昵称 / 头像 / 性别 / 邮箱 / 登录名，一个接口全包）
 *   POST /client/user/update-moblie 手机号修改（注意后端拼写是 moblie，需要短信验证码）
 *   POST /client/user/update-cipher 密码修改
 *   POST /client/user/send-sms      发短信验证码
 *   GET  /client/user/has-cipher    是否已设置密码
 *   POST /client/user/has-loginname 登录名是否已占用
 *   POST /client/user/invite-list   我邀请的人（分页）
 */

/** 当前登录用户信息（后端是 GET，不要改成 POST） */
export const getUserInfo = (): Promise<ApiResponse<AiUserInfo>> => request.get(base + '/info')

/**
 * 资料修改：昵称 / 头像 / 性别 / 邮箱 / 登录名都走这一个接口
 * @param data 只传要改的字段，后端按 Account 实体做单字段更新
 */
export const updateUser = (data: Partial<AiUserInfo>): Promise<ApiResponse<boolean>> =>
  request.post(base + '/update', data)

export const updateUserNickName = (data: { nickName: string }): Promise<ApiResponse<boolean>> => updateUser(data)

export const updateUserAvatar = (data: { avatar: string }): Promise<ApiResponse<boolean>> => updateUser(data)

export const updateUserGender = (data: { gender: string }): Promise<ApiResponse<boolean>> => updateUser(data)

export const updateUserEmail = (data: { email: string }): Promise<ApiResponse<boolean>> => updateUser(data)

/** 设置登录名（改前建议先 hasLoginName 查重） */
export const setLoginName = (loginName: string): Promise<ApiResponse<boolean>> => updateUser({ loginName })

/** 修改手机号：需要短信验证码，后端端点为 update-moblie */
export const updateUserMobile = (data: { mobile: string; code: string }): Promise<ApiResponse<boolean>> =>
  request.post(base + '/update-moblie', data)

/** 发送短信验证码（改手机号 / 改密码用）；不传 mobile 时发给当前账号手机号 */
export const sendSmsCode = (mobile?: string): Promise<ApiResponse<boolean>> =>
  request.post(base + '/send-sms', { mobile })

/** 修改密码：{ code 短信验证码, cipher 新密码 } */
export const updateUserPassword = (data: { code: string; cipher: string }): Promise<ApiResponse<boolean>> =>
  request.post(base + '/update-cipher', data)

/** 是否已设置密码 */
export const hasPassword = (): Promise<ApiResponse<boolean>> => request.get(base + '/has-cipher')

/** 登录名是否已存在 */
export const hasLoginName = (loginName: string): Promise<ApiResponse<boolean>> =>
  request.post(base + '/has-loginname', { loginName })

/** 我邀请并注册成功的人（分页） */
export const getInviteList = (query: { pageIndex?: number; pageSize?: number } = {}): Promise<ApiResponse<any>> =>
  request.post(base + '/invite-list', { pageIndex: 1, pageSize: 20, ...query })

/**
 * 解析文件访问地址
 *
 * 相对路径拼 OSS 前缀；完整 URL 原样返回，但如果页面本身是 https，
 * 而图片地址是 http（OSS 域名常见情况），浏览器会按「混合内容」直接拦截 → 图显示不出来，
 * 所以这里统一升级成 https。
 */
export function parseFileUrl(uri?: string): string {
  if (!uri || uri === '/static/default.png') return ''
  if (uri.startsWith('data:') || uri.startsWith('blob:')) return uri
  if (/^(https?:)?\/\//i.test(uri)) return upgradeToHttps(uri)
  return upgradeToHttps((APP_BASE_FILE_URL || '') + uri)
}

/** https 页面下把 http 资源升级为 https，避免混合内容被浏览器拦截 */
function upgradeToHttps(url: string): string {
  try {
    if (typeof location === 'undefined' || location.protocol !== 'https:') return url
  } catch {
    return url
  }
  return url.replace(/^http:\/\//i, 'https://')
}

/** 多张图片地址解析 */
export function parseImageList(images?: string): string[] {
  if (!images) return []
  try {
    const arr = JSON.parse(images)
    if (Array.isArray(arr)) return arr.map(parseFileUrl).filter(Boolean)
  } catch {
    /* ignore */
  }
  return images
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean)
    .map(parseFileUrl)
}

/** 解析 json 数组字符串（如 files / materials） */
export function parseJsonArray<T = any>(str?: string): T[] {
  if (!str) return []
  try {
    const v = JSON.parse(str)
    return Array.isArray(v) ? v : []
  } catch {
    return []
  }
}

/** 序列化为后端要求的 json 字符串 */
export function toJsonArray(list: any[]): string {
  return JSON.stringify(list || [])
}


